import { CommonModule } from "@angular/common";
import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { Subscription } from "rxjs";
import { LucideDynamicIcon } from "@lucide/angular";
import { backend } from "@/integrations/backend/client";
import {
  TRANSLATION_LANGUAGES,
  normalizeLocalizedTextMap,
  setLocalizedText,
  type LocalizedTextMap,
} from "@/lib/localizedContent";
import type { LanguageCode } from "../core/language.model";
import { LanguageService } from "../core/language.service";
import { AuthService, User } from "../core/auth.service";
import { ToastService } from "../core/toast.service";
import { LanguageSelectorComponent } from "../shared/language-selector.component";
import {
  badgeClasses,
  buttonClasses,
  cardClasses,
  inputClasses,
  labelClasses,
} from "../shared/ui";

interface Island {
  id: string;
  slug: string;
  name: string;
  name_translations: LocalizedTextMap;
  description: string;
  description_translations: LocalizedTextMap;
  order_index: number;
  icon: string | null;
  image_url: string | null;
  is_active: boolean;
  crew_id: string | null;
}

interface Question {
  id: string;
  island_id: string;
  question_text: string;
  question_text_translations: LocalizedTextMap;
  explanation: string | null;
  explanation_translations: LocalizedTextMap;
  order_index: number;
  stage: number;
  point_value: number;
}

interface Answer {
  id: string;
  question_id: string;
  answer_text: string;
  answer_text_translations: LocalizedTextMap;
  is_correct: boolean;
  order_index: number;
}

interface AdminUser {
  id: string;
  email: string | null;
  display_name: string | null;
  avatar_url: string | null;
  created_at: string | null;
  role: "admin" | "user";
}

interface AdminUserFallbackUser {
  id: string;
  email: string | null;
  created_at: string | null;
}

interface AdminUserFallbackProfile {
  id: string;
  display_name: string | null;
  avatar_url: string | null;
}

interface AdminUserFallbackRole {
  user_id: string;
  role: "admin" | "user";
}

interface UsageSummary {
  total_users: number;
  active_users_7d: number;
  active_users_30d: number;
  quiz_attempts: number;
  completed_quiz_attempts: number;
  avg_quiz_score_percent: number;
  completed_islands: number;
  self_assessments: number;
  avg_self_assessment_score_percent: number;
  questions_answered: number;
  incorrect_answers: number;
}

interface DailyUsageRow {
  date: string;
  active_users: number;
  quiz_attempts: number;
  answers: number;
  island_completions: number;
  self_assessments: number;
}

interface IslandPerformanceRow {
  island_id: string;
  name: string;
  slug: string;
  attempts: number;
  completed_attempts: number;
  completions: number;
  avg_score_percent: number;
  correct_rate_percent: number;
  completion_rate_percent: number;
  signal: string;
}

interface QuestionImprovementRow {
  question_id: string;
  island_name: string;
  question_text: string;
  responses: number;
  incorrect: number;
  incorrect_rate_percent: number;
  improvement_hint: string;
}

interface UserUsageRow {
  user_id: string;
  display_name: string;
  email: string | null;
  hub: string | null;
  quiz_attempts: number;
  completed_quiz_attempts: number;
  completed_islands: number;
  avg_score_percent: number;
  total_points: number;
  current_streak: number;
  last_activity_at: string | null;
}

interface HubUsageRow {
  hub: string;
  users: number;
  active_users_30d: number;
  completed_islands: number;
  avg_score_percent: number;
}

interface UsageAnalyticsRow {
  section: string;
  payload: unknown;
}

interface AnswerDraft {
  answer_text: string;
  answer_text_translations: LocalizedTextMap;
  is_correct: boolean;
}

const ADMIN_TEXT_EN = {
  "header.title": "Island Admin",
  "header.description": "Manage islands, questions, answers and admins.",
  "tabs.content": "Islands & Questions",
  "tabs.usage": "Usage",
  "tabs.users": "Users",
  "action.newIsland": "New Island",
  "action.refresh": "Refresh",
  "action.delete": "Delete",
  "action.upload": "Upload",
  "action.uploading": "Uploading...",
  "action.saveIsland": "Save Island",
  "action.saveQuestion": "Save Question",
  "action.cancel": "Cancel",
  "action.createIsland": "Create Island",
  "action.createQuestion": "Create Question",
  "action.saving": "Saving...",
  "action.creating": "Creating...",
  "status.active": "Active",
  "status.hidden": "Hidden",
  "content.islands": "Islands",
  "content.name": "Name",
  "content.slug": "Slug",
  "content.description": "Description",
  "content.icon": "Icon",
  "content.order": "Order",
  "content.imageUrl": "Image URL",
  "content.translations": "Translations",
  "content.questions": "Questions",
  "content.questionsDescription":
    "Create and edit quiz content for this island.",
  "content.newQuestion": "New Question",
  "content.questionText": "Question text",
  "content.explanation": "Explanation",
  "content.points": "Points",
  "content.answer": "Answer {index}",
  "content.stage": "Stage {stage}",
  "content.createIslandTitle": "Create New Island",
  "content.createIslandDescription":
    "Add a new island to the learning journey.",
  "content.requiredName": "Name *",
  "content.requiredSlug": "Slug *",
  "content.requiredDescription": "Description *",
  "usage.title": "Usage Analytics",
  "usage.description": "Activity, progress and content improvement signals.",
  "usage.totalUsers": "Total users",
  "usage.activeIn30Days": "{count} active in 30 days",
  "usage.quizAttempts": "Quiz attempts",
  "usage.completed": "{count} completed",
  "usage.averageQuizScore": "Average quiz score",
  "usage.answersTracked": "{count} answers tracked",
  "usage.islandsCompleted": "Islands completed",
  "usage.selfAssessments": "{count} self assessments",
  "usage.dailyUsage": "Daily usage",
  "usage.dailyUsageDescription": "Last 14 days by activity volume.",
  "usage.activeIn7Days": "{count} active in 7 days",
  "usage.dailyLine": "{users} users - {attempts} attempts - {answers} answers",
  "usage.noActivity": "No activity recorded yet.",
  "usage.byHub": "Usage by hub",
  "usage.byHubDescription": "Participation and outcomes by location.",
  "usage.hubUsers": "{count} users",
  "usage.hubLine": "{active} active - score {score}% - {islands} islands",
  "usage.noHub": "No hub data recorded yet.",
  "usage.islandPerformance": "Island performance",
  "usage.islandPerformanceDescription":
    "Score, accuracy and completion signals by island.",
  "usage.accuracy": "Accuracy",
  "usage.completion": "Completion",
  "usage.islandStats":
    "{attempts} attempts - {completions} completions - average score {score}%",
  "usage.noIslandPerformance": "No island performance data recorded yet.",
  "usage.improvementPoints": "Improvement points",
  "usage.improvementDescription":
    "Questions with the strongest incorrect-answer signal.",
  "usage.percentWrong": "{percent}% wrong",
  "usage.incorrectLine": "{incorrect} of {responses} answers were incorrect",
  "usage.noQuestionData": "No question response data recorded yet.",
  "usage.mostActiveUsers": "Most active users",
  "usage.mostActiveDescription": "Ordered by recent activity and progress.",
  "usage.tableUser": "User",
  "usage.tableProgress": "Progress",
  "usage.tableScore": "Score",
  "usage.tableLastActivity": "Last activity",
  "usage.userIslands": "{count} islands",
  "usage.userAttemptStreak": "{attempts} attempts - {streak} day streak",
  "usage.averageShort": "avg {score}%",
  "usage.noLastActivity": "No activity",
  "usage.noUserActivity": "No user activity recorded yet.",
  "usage.signal.healthy": "Healthy",
  "usage.signal.noUsage": "No usage yet",
  "usage.signal.lowScore": "Low average score",
  "usage.signal.incorrect": "Many incorrect answers",
  "usage.signal.lowCompletion": "Low completion",
  "usage.hint.collectMore": "Collect more answers",
  "usage.hint.review": "Review prompt and alternatives",
  "usage.hint.addSupport": "Add reinforcement or example",
  "usage.hint.monitor": "Monitor trend",
  "users.title": "Users",
  "users.description": "Promote, demote or delete player accounts.",
  "users.admin": "Admin",
  "users.user": "User",
  "users.noEmail": "No email available",
  "toast.accessDenied": "Access denied. You must be a crew owner or admin.",
  "toast.cloneError": "Failed to initialize island templates",
  "toast.cloneSuccess": "Island templates loaded.",
  "toast.loadIslandsError": "Failed to load islands",
  "toast.questionsLoadError":
    "Could not load questions. Refresh the page or check your admin permissions.",
  "toast.usersUnavailable":
    "User management RPC is not available yet. Apply the latest migration first.",
  "toast.usageUnavailable":
    "Usage analytics are not available yet. Apply the latest backend migration first.",
  "toast.imageOnly": "Please choose an image file",
  "toast.imageUploaded": "Island image uploaded",
  "toast.imageUploadError":
    "Failed to upload image. Apply the storage migration first.",
  "toast.islandUpdated": "Island updated successfully",
  "toast.saveError": "Failed to save changes",
  "toast.requiredFields": "Please fill in all required fields",
  "toast.crewRequired": "You must create or join a crew first",
  "toast.islandCreated": "Island created successfully",
  "toast.duplicateSlug": "An island with this slug already exists",
  "toast.createIslandError": "Failed to create island",
  "toast.deleteIslandConfirm":
    'Are you sure you want to delete "{name}"? This action cannot be undone.',
  "toast.islandDeleted": "Island deleted successfully",
  "toast.deleteIslandError":
    "Failed to delete island. It may have associated content.",
  "toast.selectQuestion": "Select an island and write the question",
  "toast.addAnswers": "Add at least two answers",
  "toast.markCorrect": "Mark one answer as correct",
  "toast.questionCreated": "Question created successfully",
  "toast.createQuestionError": "Failed to create question",
  "toast.questionUpdated": "Question updated",
  "toast.saveQuestionError": "Failed to save question",
  "toast.deleteQuestionConfirm": "Delete this question and its answers?",
  "toast.questionDeleted": "Question deleted",
  "toast.deleteQuestionError": "Failed to delete question",
  "toast.roleUpdated": "User role updated",
  "toast.roleUpdateError": "Failed to update user role",
  "toast.deleteOwnAdmin":
    "You cannot delete your own admin account from this screen.",
  "toast.deleteUserConfirm":
    'Delete user "{name}" and all related progress? This action cannot be undone.',
  "toast.userDeleted": "User deleted",
  "toast.deleteUserError": "Failed to delete user",
} as const;

type AdminTextKey = keyof typeof ADMIN_TEXT_EN;

const ADMIN_TEXT: Record<
  LanguageCode,
  Partial<Record<AdminTextKey, string>>
> = {
  en: ADMIN_TEXT_EN,
  pt: {
    "header.title": "Administracao das Ilhas",
    "header.description":
      "Gerencie ilhas, perguntas, respostas e administradores.",
    "tabs.content": "Ilhas e Perguntas",
    "tabs.usage": "Uso",
    "tabs.users": "Usuarios",
    "action.newIsland": "Nova Ilha",
    "action.refresh": "Atualizar",
    "action.delete": "Excluir",
    "action.upload": "Enviar",
    "action.uploading": "Enviando...",
    "action.saveIsland": "Salvar Ilha",
    "action.saveQuestion": "Salvar Pergunta",
    "action.cancel": "Cancelar",
    "action.createIsland": "Criar Ilha",
    "action.createQuestion": "Criar Pergunta",
    "action.saving": "Salvando...",
    "action.creating": "Criando...",
    "status.active": "Ativa",
    "status.hidden": "Oculta",
    "content.islands": "Ilhas",
    "content.name": "Nome",
    "content.slug": "Slug",
    "content.description": "Descricao",
    "content.icon": "Icone",
    "content.order": "Ordem",
    "content.imageUrl": "URL da imagem",
    "content.translations": "Traducoes",
    "content.questions": "Perguntas",
    "content.questionsDescription":
      "Crie e edite o conteudo do quiz para esta ilha.",
    "content.newQuestion": "Nova Pergunta",
    "content.questionText": "Texto da pergunta",
    "content.explanation": "Explicacao",
    "content.points": "Pontos",
    "content.answer": "Resposta {index}",
    "content.stage": "Etapa {stage}",
    "content.createIslandTitle": "Criar Nova Ilha",
    "content.createIslandDescription":
      "Adicione uma nova ilha a jornada de aprendizado.",
    "content.requiredName": "Nome *",
    "content.requiredSlug": "Slug *",
    "content.requiredDescription": "Descricao *",
    "usage.title": "Analise de Uso",
    "usage.description": "Atividade, progresso e sinais de melhoria.",
    "usage.totalUsers": "Total de usuarios",
    "usage.activeIn30Days": "{count} ativos em 30 dias",
    "usage.quizAttempts": "Tentativas de quiz",
    "usage.completed": "{count} concluidas",
    "usage.averageQuizScore": "Media do quiz",
    "usage.answersTracked": "{count} respostas registradas",
    "usage.islandsCompleted": "Ilhas concluidas",
    "usage.selfAssessments": "{count} autoavaliacoes",
    "usage.dailyUsage": "Uso diario",
    "usage.dailyUsageDescription": "Ultimos 14 dias por volume de atividade.",
    "usage.activeIn7Days": "{count} ativos em 7 dias",
    "usage.dailyLine":
      "{users} usuarios - {attempts} tentativas - {answers} respostas",
    "usage.noActivity": "Nenhuma atividade registrada ainda.",
    "usage.byHub": "Uso por hub",
    "usage.byHubDescription": "Participacao e resultados por localidade.",
    "usage.hubUsers": "{count} usuarios",
    "usage.hubLine": "{active} ativos - nota {score}% - {islands} ilhas",
    "usage.noHub": "Nenhum dado de hub registrado ainda.",
    "usage.islandPerformance": "Desempenho por ilha",
    "usage.islandPerformanceDescription":
      "Sinais de nota, acerto e conclusao por ilha.",
    "usage.accuracy": "Acerto",
    "usage.completion": "Conclusao",
    "usage.islandStats":
      "{attempts} tentativas - {completions} conclusoes - nota media {score}%",
    "usage.noIslandPerformance": "Nenhum desempenho por ilha registrado ainda.",
    "usage.improvementPoints": "Pontos de melhoria",
    "usage.improvementDescription":
      "Perguntas com maior sinal de respostas incorretas.",
    "usage.percentWrong": "{percent}% erradas",
    "usage.incorrectLine":
      "{incorrect} de {responses} respostas foram incorretas",
    "usage.noQuestionData": "Nenhum dado de resposta registrado ainda.",
    "usage.mostActiveUsers": "Usuarios mais ativos",
    "usage.mostActiveDescription": "Ordenado por atividade recente e progresso.",
    "usage.tableUser": "Usuario",
    "usage.tableProgress": "Progresso",
    "usage.tableScore": "Pontuacao",
    "usage.tableLastActivity": "Ultima atividade",
    "usage.userIslands": "{count} ilhas",
    "usage.userAttemptStreak":
      "{attempts} tentativas - sequencia de {streak} dia(s)",
    "usage.averageShort": "media {score}%",
    "usage.noLastActivity": "Sem atividade",
    "usage.noUserActivity": "Nenhuma atividade de usuario registrada ainda.",
    "usage.signal.healthy": "Saudavel",
    "usage.signal.noUsage": "Sem uso ainda",
    "usage.signal.lowScore": "Media baixa",
    "usage.signal.incorrect": "Muitas respostas incorretas",
    "usage.signal.lowCompletion": "Baixa conclusao",
    "usage.hint.collectMore": "Coletar mais respostas",
    "usage.hint.review": "Revisar enunciado e alternativas",
    "usage.hint.addSupport": "Adicionar reforco ou exemplo",
    "usage.hint.monitor": "Monitorar tendencia",
    "users.title": "Usuarios",
    "users.description": "Promova, rebaixe ou exclua contas de jogadores.",
    "users.admin": "Admin",
    "users.user": "Usuario",
    "users.noEmail": "Sem email disponivel",
    "toast.accessDenied":
      "Acesso negado. Voce precisa ser dono de crew ou admin.",
    "toast.cloneError": "Falha ao inicializar modelos de ilhas",
    "toast.cloneSuccess": "Modelos de ilhas carregados.",
    "toast.loadIslandsError": "Falha ao carregar ilhas",
    "toast.questionsLoadError":
      "Nao foi possivel carregar perguntas. Atualize a pagina ou verifique suas permissoes.",
    "toast.usersUnavailable":
      "O RPC de usuarios ainda nao esta disponivel. Aplique a migracao mais recente.",
    "toast.usageUnavailable":
      "A analise de uso ainda nao esta disponivel. Aplique a migracao mais recente do backend.",
    "toast.imageOnly": "Escolha um arquivo de imagem",
    "toast.imageUploaded": "Imagem da ilha enviada",
    "toast.imageUploadError":
      "Falha ao enviar imagem. Aplique a migracao de storage primeiro.",
    "toast.islandUpdated": "Ilha atualizada com sucesso",
    "toast.saveError": "Falha ao salvar alteracoes",
    "toast.requiredFields": "Preencha todos os campos obrigatorios",
    "toast.crewRequired": "Voce precisa criar ou entrar em uma crew primeiro",
    "toast.islandCreated": "Ilha criada com sucesso",
    "toast.duplicateSlug": "Ja existe uma ilha com este slug",
    "toast.createIslandError": "Falha ao criar ilha",
    "toast.deleteIslandConfirm":
      'Tem certeza que deseja excluir "{name}"? Esta acao nao pode ser desfeita.',
    "toast.islandDeleted": "Ilha excluida com sucesso",
    "toast.deleteIslandError":
      "Falha ao excluir ilha. Ela pode ter conteudo associado.",
    "toast.selectQuestion": "Selecione uma ilha e escreva a pergunta",
    "toast.addAnswers": "Adicione pelo menos duas respostas",
    "toast.markCorrect": "Marque uma resposta como correta",
    "toast.questionCreated": "Pergunta criada com sucesso",
    "toast.createQuestionError": "Falha ao criar pergunta",
    "toast.questionUpdated": "Pergunta atualizada",
    "toast.saveQuestionError": "Falha ao salvar pergunta",
    "toast.deleteQuestionConfirm": "Excluir esta pergunta e suas respostas?",
    "toast.questionDeleted": "Pergunta excluida",
    "toast.deleteQuestionError": "Falha ao excluir pergunta",
    "toast.roleUpdated": "Papel do usuario atualizado",
    "toast.roleUpdateError": "Falha ao atualizar papel do usuario",
    "toast.deleteOwnAdmin":
      "Voce nao pode excluir sua propria conta admin por esta tela.",
    "toast.deleteUserConfirm":
      'Excluir usuario "{name}" e todo o progresso relacionado? Esta acao nao pode ser desfeita.',
    "toast.userDeleted": "Usuario excluido",
    "toast.deleteUserError": "Falha ao excluir usuario",
  },
  nl: {},
  sv: {},
};

function interpolateAdminText(
  template: string,
  params?: Record<string, string | number>,
) {
  if (!params) return template;

  return Object.entries(params).reduce(
    (text, [key, value]) => text.replaceAll(`{${key}}`, String(value)),
    template,
  );
}

const emptyUsageSummary = (): UsageSummary => ({
  total_users: 0,
  active_users_7d: 0,
  active_users_30d: 0,
  quiz_attempts: 0,
  completed_quiz_attempts: 0,
  avg_quiz_score_percent: 0,
  completed_islands: 0,
  self_assessments: 0,
  avg_self_assessment_score_percent: 0,
  questions_answered: 0,
  incorrect_answers: 0,
});

const emptyAnswerDrafts = (): AnswerDraft[] => [
  { answer_text: "", answer_text_translations: {}, is_correct: true },
  { answer_text: "", answer_text_translations: {}, is_correct: false },
  { answer_text: "", answer_text_translations: {}, is_correct: false },
  { answer_text: "", answer_text_translations: {}, is_correct: false },
];

const normalizeQuestionPointValue = (value: string | number) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue)
    ? Math.max(1, Math.round(numericValue))
    : 10;
};

const withEnglishFallback = (
  translations: LocalizedTextMap,
  englishValue: string | null | undefined,
) => {
  const normalized = normalizeLocalizedTextMap(translations);
  const trimmedEnglishValue = englishValue?.trim();

  if (trimmedEnglishValue) {
    normalized.en = trimmedEnglishValue;
  } else {
    delete normalized.en;
  }

  return normalized;
};

function chunkArray<T>(items: T[], size: number) {
  const chunks: T[][] = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
}

function sectionArray<T>(sections: Map<string, unknown>, key: string): T[] {
  const value = sections.get(key);
  return Array.isArray(value) ? (value as T[]) : [];
}

@Component({
  selector: "app-admin-islands-page",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LanguageSelectorComponent,
    LucideDynamicIcon,
  ],
  template: `
    <div
      *ngIf="authLoading || loading"
      class="min-h-screen flex items-center justify-center ocean-gradient"
    >
      <svg
        lucideIcon="loader-circle"
        class="h-8 w-8 animate-spin text-primary-foreground"
      ></svg>
    </div>

    <div
      *ngIf="!authLoading && !loading"
      class="min-h-screen bg-background bg-ocean-pattern"
    >
      <header
        class="sticky top-0 z-50 border-b border-border bg-background/85 backdrop-blur-md"
      >
        <div class="container mx-auto max-w-6xl px-4 py-4">
          <div class="flex items-center justify-between gap-4">
            <div class="flex items-center gap-4">
              <button
                [class]="buttonClasses('ghost', 'icon')"
                (click)="router.navigateByUrl('/')"
              >
                <svg lucideIcon="arrow-left" class="h-5 w-5"></svg>
              </button>
              <div>
                <h1 class="text-xl font-bold text-foreground">
                  {{ adminText("header.title") }}
                </h1>
                <p class="text-sm text-muted-foreground">
                  {{ adminText("header.description") }}
                </p>
              </div>
            </div>
            <div class="flex flex-wrap items-center justify-end gap-2">
              <button
                *ngIf="activeAdminTab === 'content'"
                [class]="buttonClasses('default')"
                (click)="newIslandOpen = true"
              >
                <svg lucideIcon="plus" class="h-4 w-4"></svg>
                {{ adminText("action.newIsland") }}
              </button>
            </div>
          </div>
        </div>
      </header>

      <main class="container mx-auto max-w-6xl px-4 py-8">
        <div class="mb-6 flex flex-wrap gap-2 border-b border-border">
          <button
            type="button"
            class="flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-semibold transition"
            [ngClass]="
              activeAdminTab === 'content'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            "
            (click)="activeAdminTab = 'content'"
          >
            <svg lucideIcon="map" class="h-4 w-4"></svg>
            {{ adminText("tabs.content") }}
          </button>
          <button
            *ngIf="isAdmin"
            type="button"
            class="flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-semibold transition"
            [ngClass]="
              activeAdminTab === 'usage'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            "
            (click)="activeAdminTab = 'usage'"
          >
            <svg lucideIcon="target" class="h-4 w-4"></svg>
            {{ adminText("tabs.usage") }}
          </button>
          <button
            *ngIf="isAdmin"
            type="button"
            class="flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-semibold transition"
            [ngClass]="
              activeAdminTab === 'users'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            "
            (click)="activeAdminTab = 'users'"
          >
            <svg lucideIcon="users" class="h-4 w-4"></svg>
            {{ adminText("tabs.users") }}
          </button>
          <div class="ml-auto flex items-center pb-2">
            <app-language-selector
              [compact]="true"
              className="bg-background/90"
            />
          </div>
        </div>

        <div
          *ngIf="activeAdminTab === 'content'"
          class="grid gap-6 lg:grid-cols-[21rem_1fr]"
        >
          <aside [class]="cardClasses('p-4 h-fit')">
            <div class="mb-4 flex items-center justify-between">
              <h2 class="text-sm font-semibold text-foreground">
                {{ adminText("content.islands") }}
              </h2>
              <span [class]="badgeClasses('secondary')">{{
                islands.length
              }}</span>
            </div>
            <div class="space-y-2">
              <button
                *ngFor="let island of islands"
                type="button"
                (click)="selectedIslandId = island.id"
                class="w-full rounded-md border p-3 text-left transition hover:bg-muted"
                [ngClass]="
                  selectedIslandId === island.id
                    ? 'border-primary bg-primary/10'
                    : 'border-border bg-card'
                "
              >
                <div class="flex items-center justify-between gap-2">
                  <span class="font-medium truncate">{{ island.name }}</span>
                  <span
                    [class]="
                      badgeClasses(
                        island.is_active ? 'secondary' : 'outline',
                        'text-[10px]'
                      )
                    "
                  >
                    {{
                      island.is_active
                        ? adminText("status.active")
                        : adminText("status.hidden")
                    }}
                  </span>
                </div>
                <p class="mt-1 truncate text-xs text-muted-foreground">
                  {{ island.slug }}
                </p>
              </button>
            </div>
          </aside>

          <section class="space-y-6" *ngIf="selectedIsland as island">
            <div [class]="cardClasses()">
              <header class="p-6">
                <div class="flex items-start justify-between gap-4">
                  <div>
                    <h2
                      class="text-2xl font-semibold leading-none tracking-normal"
                    >
                      {{ island.name }}
                    </h2>
                    <p class="mt-2 text-sm text-muted-foreground">
                      {{ island.description }}
                    </p>
                  </div>
                  <button
                    [class]="buttonClasses('destructive', 'sm')"
                    (click)="handleDeleteIsland(island.id, island.name)"
                  >
                    <svg lucideIcon="trash-2" class="h-4 w-4"></svg>
                    {{ adminText("action.delete") }}
                  </button>
                </div>
              </header>
              <div class="grid gap-4 p-6 pt-0 md:grid-cols-2">
                <div class="space-y-2">
                  <label [class]="labelClasses()">{{
                    adminText("content.name")
                  }}</label>
                  <input
                    [class]="inputClasses()"
                    [ngModel]="draftIsland(island).name"
                    (ngModelChange)="
                      handleFieldChange(island.id, 'name', $event)
                    "
                  />
                </div>
                <div class="space-y-2">
                  <label [class]="labelClasses()">{{
                    adminText("content.slug")
                  }}</label>
                  <input
                    [class]="inputClasses()"
                    [ngModel]="draftIsland(island).slug"
                    (ngModelChange)="
                      handleFieldChange(island.id, 'slug', $event)
                    "
                  />
                </div>
                <div class="space-y-2 md:col-span-2">
                  <label [class]="labelClasses()">{{
                    adminText("content.description")
                  }}</label>
                  <textarea
                    [class]="inputClasses('min-h-24')"
                    [ngModel]="draftIsland(island).description"
                    (ngModelChange)="
                      handleFieldChange(island.id, 'description', $event)
                    "
                  ></textarea>
                </div>
                <div class="space-y-2">
                  <label [class]="labelClasses()">{{
                    adminText("content.icon")
                  }}</label>
                  <input
                    [class]="inputClasses()"
                    [ngModel]="draftIsland(island).icon || ''"
                    (ngModelChange)="
                      handleFieldChange(island.id, 'icon', $event)
                    "
                  />
                </div>
                <div class="space-y-2">
                  <label [class]="labelClasses()">{{
                    adminText("content.order")
                  }}</label>
                  <input
                    [class]="inputClasses()"
                    type="number"
                    [ngModel]="draftIsland(island).order_index"
                    (ngModelChange)="
                      handleFieldChange(
                        island.id,
                        'order_index',
                        numberValue($event)
                      )
                    "
                  />
                </div>
                <div class="space-y-2 md:col-span-2">
                  <label [class]="labelClasses()">{{
                    adminText("content.imageUrl")
                  }}</label>
                  <div class="flex gap-2">
                    <input
                      [class]="inputClasses()"
                      [ngModel]="draftIsland(island).image_url || ''"
                      (ngModelChange)="
                        handleFieldChange(island.id, 'image_url', $event)
                      "
                    />
                    <label
                      [class]="buttonClasses('outline')"
                      [for]="'image-upload-' + island.id"
                    >
                      <svg lucideIcon="upload" class="h-4 w-4"></svg>
                      {{
                        uploadingImageFor === island.id
                          ? adminText("action.uploading")
                          : adminText("action.upload")
                      }}
                    </label>
                    <input
                      [id]="'image-upload-' + island.id"
                      type="file"
                      accept="image/*"
                      class="hidden"
                      (change)="
                        uploadIslandImage(
                          $any($event.target).files?.[0] || null,
                          island.id
                        )
                      "
                    />
                  </div>
                </div>
                <label class="flex items-center gap-2 md:col-span-2">
                  <input
                    type="checkbox"
                    class="h-4 w-4"
                    [ngModel]="draftIsland(island).is_active"
                    (ngModelChange)="
                      handleFieldChange(island.id, 'is_active', $event)
                    "
                  />
                  <span class="text-sm font-medium">{{
                    adminText("status.active")
                  }}</span>
                </label>
                <div class="md:col-span-2 rounded-md border bg-muted/30 p-4">
                  <h3 class="text-sm font-semibold text-foreground">
                    {{ adminText("content.translations") }}
                  </h3>
                  <div class="mt-4 grid gap-4 md:grid-cols-2">
                    <div
                      *ngFor="let lang of translationLanguages"
                      class="space-y-3 rounded-md bg-background/80 p-3"
                    >
                      <p class="text-xs font-semibold text-muted-foreground">
                        {{ lang.label }}
                      </p>
                      <input
                        [class]="inputClasses()"
                        [placeholder]="adminText('content.name')"
                        [ngModel]="
                          getTranslation(
                            draftIsland(island).name_translations,
                            lang.code
                          )
                        "
                        (ngModelChange)="
                          setIslandTranslation(
                            island.id,
                            'name_translations',
                            lang.code,
                            $event
                          )
                        "
                      />
                      <textarea
                        [class]="inputClasses('min-h-20')"
                        [placeholder]="adminText('content.description')"
                        [ngModel]="
                          getTranslation(
                            draftIsland(island).description_translations,
                            lang.code
                          )
                        "
                        (ngModelChange)="
                          setIslandTranslation(
                            island.id,
                            'description_translations',
                            lang.code,
                            $event
                          )
                        "
                      ></textarea>
                    </div>
                  </div>
                </div>
                <div class="md:col-span-2 flex justify-end">
                  <button
                    [class]="buttonClasses('default')"
                    [disabled]="
                      !editedIslands[island.id] || saving === island.id
                    "
                    (click)="handleSave(island.id)"
                  >
                    <svg lucideIcon="save" class="h-4 w-4"></svg>
                    {{
                      saving === island.id
                        ? adminText("action.saving")
                        : adminText("action.saveIsland")
                    }}
                  </button>
                </div>
              </div>
            </div>

            <div [class]="cardClasses()">
              <header class="p-6">
                <h2 class="text-2xl font-semibold leading-none tracking-normal">
                  {{ adminText("content.questions") }}
                </h2>
                <p class="mt-2 text-sm text-muted-foreground">
                  {{ adminText("content.questionsDescription") }}
                </p>
              </header>
              <div class="p-6 pt-0 space-y-6">
                <p
                  *ngIf="questionsError"
                  class="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive"
                >
                  {{ questionsError }}
                </p>
                <div class="rounded-md border bg-muted/30 p-4">
                  <h3 class="text-sm font-semibold">
                    {{ adminText("content.newQuestion") }}
                  </h3>
                  <div class="mt-4 grid gap-3">
                    <textarea
                      [class]="inputClasses('min-h-20')"
                      [placeholder]="adminText('content.questionText')"
                      [(ngModel)]="newQuestion.question_text"
                    ></textarea>
                    <textarea
                      [class]="inputClasses('min-h-20')"
                      [placeholder]="adminText('content.explanation')"
                      [(ngModel)]="newQuestion.explanation"
                    ></textarea>
                    <div class="grid gap-3 md:grid-cols-2">
                      <select
                        [class]="inputClasses()"
                        [(ngModel)]="newQuestion.stage"
                      >
                        <option value="1">
                          {{ adminText("content.stage", { stage: 1 }) }}
                        </option>
                        <option value="2">
                          {{ adminText("content.stage", { stage: 2 }) }}
                        </option>
                        <option value="3">
                          {{ adminText("content.stage", { stage: 3 }) }}
                        </option>
                      </select>
                      <input
                        [class]="inputClasses()"
                        type="number"
                        min="1"
                        [(ngModel)]="newQuestion.point_value"
                        [placeholder]="adminText('content.points')"
                      />
                    </div>
                    <div class="grid gap-3">
                      <div
                        *ngFor="let answer of newAnswers; let index = index"
                        class="flex items-center gap-2"
                      >
                        <input
                          type="radio"
                          name="correctNewAnswer"
                          [checked]="answer.is_correct"
                          (change)="setNewCorrectAnswer(index)"
                        />
                        <input
                          [class]="inputClasses()"
                          [(ngModel)]="answer.answer_text"
                          [placeholder]="
                            adminText('content.answer', { index: index + 1 })
                          "
                        />
                      </div>
                    </div>
                    <div class="flex justify-end">
                      <button
                        [class]="buttonClasses('default')"
                        [disabled]="creatingQuestion"
                        (click)="handleCreateQuestion()"
                      >
                        <svg lucideIcon="plus" class="h-4 w-4"></svg>
                        {{
                          creatingQuestion
                            ? adminText("action.creating")
                            : adminText("action.createQuestion")
                        }}
                      </button>
                    </div>
                  </div>
                </div>

                <div class="space-y-4">
                  <article
                    *ngFor="let question of selectedQuestions"
                    class="rounded-md border bg-card p-4"
                  >
                    <div class="grid gap-3">
                      <div class="flex items-start justify-between gap-3">
                        <div class="flex flex-wrap gap-2">
                          <span [class]="badgeClasses('secondary')"
                            >{{
                              adminText("content.stage", {
                                stage: question.stage,
                              })
                            }}</span
                          >
                          <span [class]="badgeClasses('outline')"
                            >{{ question.point_value }}
                            {{ language.t("common.pointsShort") }}</span
                          >
                        </div>
                        <button
                          [class]="
                            buttonClasses(
                              'ghost',
                              'sm',
                              'text-destructive hover:text-destructive'
                            )
                          "
                          (click)="handleDeleteQuestion(question.id)"
                        >
                          <svg lucideIcon="trash-2" class="h-4 w-4"></svg>
                        </button>
                      </div>
                      <textarea
                        [class]="inputClasses('min-h-20')"
                        [ngModel]="question.question_text"
                        (ngModelChange)="
                          handleQuestionChange(
                            question.id,
                            'question_text',
                            $event
                          )
                        "
                      ></textarea>
                      <textarea
                        [class]="inputClasses('min-h-20')"
                        [ngModel]="question.explanation || ''"
                        (ngModelChange)="
                          handleQuestionChange(
                            question.id,
                            'explanation',
                            $event
                          )
                        "
                      ></textarea>
                      <div class="grid gap-3 md:grid-cols-2">
                        <select
                          [class]="inputClasses()"
                          [ngModel]="question.stage"
                          (ngModelChange)="
                            handleQuestionChange(
                              question.id,
                              'stage',
                              numberValue($event)
                            )
                          "
                        >
                          <option [ngValue]="1">
                            {{ adminText("content.stage", { stage: 1 }) }}
                          </option>
                          <option [ngValue]="2">
                            {{ adminText("content.stage", { stage: 2 }) }}
                          </option>
                          <option [ngValue]="3">
                            {{ adminText("content.stage", { stage: 3 }) }}
                          </option>
                        </select>
                        <input
                          [class]="inputClasses()"
                          type="number"
                          min="1"
                          [ngModel]="question.point_value"
                          (ngModelChange)="
                            handleQuestionChange(
                              question.id,
                              'point_value',
                              numberValue($event)
                            )
                          "
                        />
                      </div>
                      <div class="space-y-2">
                        <div
                          *ngFor="let answer of answersForQuestion(question.id)"
                          class="flex items-center gap-2"
                        >
                          <input
                            type="radio"
                            [name]="'correct-' + question.id"
                            [checked]="answer.is_correct"
                            (change)="markAnswerCorrect(question.id, answer.id)"
                          />
                          <input
                            [class]="inputClasses()"
                            [ngModel]="answer.answer_text"
                            (ngModelChange)="
                              handleAnswerChange(answer.id, $event)
                            "
                          />
                        </div>
                      </div>
                      <div class="flex justify-end">
                        <button
                          [class]="buttonClasses('default', 'sm')"
                          [disabled]="saving === question.id"
                          (click)="handleSaveQuestion(question)"
                        >
                          <svg lucideIcon="save" class="h-4 w-4"></svg>
                          {{
                            saving === question.id
                              ? adminText("action.saving")
                              : adminText("action.saveQuestion")
                          }}
                        </button>
                      </div>
                    </div>
                  </article>
                </div>
              </div>
            </div>
          </section>
        </div>

        <section
          *ngIf="activeAdminTab === 'usage' && isAdmin"
          class="space-y-6"
        >
          <div
            class="flex flex-col gap-3 md:flex-row md:items-center md:justify-between"
          >
            <div>
              <h2 class="text-2xl font-semibold leading-none tracking-normal">
                {{ adminText("usage.title") }}
              </h2>
              <p class="mt-2 text-sm text-muted-foreground">
                {{ adminText("usage.description") }}
              </p>
            </div>
            <button
              [class]="buttonClasses('outline')"
              [disabled]="usageLoading"
              (click)="fetchUsageAnalytics()"
            >
              <svg
                [lucideIcon]="usageLoading ? 'loader-circle' : 'rotate-cw'"
                class="h-4 w-4"
                [ngClass]="{ 'animate-spin': usageLoading }"
              ></svg>
              {{ adminText("action.refresh") }}
            </button>
          </div>

          <p
            *ngIf="usageError"
            class="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive"
          >
            {{ usageError }}
          </p>

          <div
            *ngIf="usageLoading && !usageLoaded"
            [class]="cardClasses('p-8 text-center')"
          >
            <svg
              lucideIcon="loader-circle"
              class="mx-auto h-8 w-8 animate-spin text-primary"
            ></svg>
          </div>

          <ng-container *ngIf="usageLoaded">
            <div class="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <article [class]="cardClasses('p-4')">
                <div class="flex items-center justify-between gap-3">
                  <p class="text-sm font-medium text-muted-foreground">
                    {{ adminText("usage.totalUsers") }}
                  </p>
                  <svg lucideIcon="users" class="h-4 w-4 text-primary"></svg>
                </div>
                <p class="mt-3 text-3xl font-semibold text-foreground">
                  {{ usageSummary.total_users }}
                </p>
                <p class="mt-1 text-xs text-muted-foreground">
                  {{
                    adminText("usage.activeIn30Days", {
                      count: usageSummary.active_users_30d,
                    })
                  }}
                </p>
              </article>
              <article [class]="cardClasses('p-4')">
                <div class="flex items-center justify-between gap-3">
                  <p class="text-sm font-medium text-muted-foreground">
                    {{ adminText("usage.quizAttempts") }}
                  </p>
                  <svg
                    lucideIcon="clipboard-list"
                    class="h-4 w-4 text-primary"
                  ></svg>
                </div>
                <p class="mt-3 text-3xl font-semibold text-foreground">
                  {{ usageSummary.quiz_attempts }}
                </p>
                <p class="mt-1 text-xs text-muted-foreground">
                  {{
                    adminText("usage.completed", {
                      count: usageSummary.completed_quiz_attempts,
                    })
                  }}
                </p>
              </article>
              <article [class]="cardClasses('p-4')">
                <div class="flex items-center justify-between gap-3">
                  <p class="text-sm font-medium text-muted-foreground">
                    {{ adminText("usage.averageQuizScore") }}
                  </p>
                  <svg lucideIcon="target" class="h-4 w-4 text-primary"></svg>
                </div>
                <p class="mt-3 text-3xl font-semibold text-foreground">
                  {{ usageSummary.avg_quiz_score_percent }}%
                </p>
                <p class="mt-1 text-xs text-muted-foreground">
                  {{
                    adminText("usage.answersTracked", {
                      count: usageSummary.questions_answered,
                    })
                  }}
                </p>
              </article>
              <article [class]="cardClasses('p-4')">
                <div class="flex items-center justify-between gap-3">
                  <p class="text-sm font-medium text-muted-foreground">
                    {{ adminText("usage.islandsCompleted") }}
                  </p>
                  <svg lucideIcon="flag" class="h-4 w-4 text-primary"></svg>
                </div>
                <p class="mt-3 text-3xl font-semibold text-foreground">
                  {{ usageSummary.completed_islands }}
                </p>
                <p class="mt-1 text-xs text-muted-foreground">
                  {{
                    adminText("usage.selfAssessments", {
                      count: usageSummary.self_assessments,
                    })
                  }}
                </p>
              </article>
            </div>

            <div class="grid gap-6 xl:grid-cols-[1.15fr_0.85fr]">
              <section [class]="cardClasses('p-6')">
                <header
                  class="mb-5 flex flex-col gap-2 md:flex-row md:items-center md:justify-between"
                >
                  <div>
                    <h3 class="text-lg font-semibold">
                      {{ adminText("usage.dailyUsage") }}
                    </h3>
                    <p class="text-sm text-muted-foreground">
                      {{ adminText("usage.dailyUsageDescription") }}
                    </p>
                  </div>
                  <span [class]="badgeClasses('secondary')">
                    {{
                      adminText("usage.activeIn7Days", {
                        count: usageSummary.active_users_7d,
                      })
                    }}
                  </span>
                </header>
                <div class="space-y-3">
                  <div
                    *ngFor="let day of dailyUsage"
                    class="grid gap-2 md:grid-cols-[4.5rem_1fr_11rem] md:items-center"
                  >
                    <p class="text-sm font-medium text-muted-foreground">
                      {{ day.date | date: "dd/MM" }}
                    </p>
                    <div class="h-3 overflow-hidden rounded-full bg-muted">
                      <div
                        class="h-full rounded-full bg-primary transition-all"
                        [ngStyle]="barStyle(dailyTotal(day), maxDailyTotal)"
                      ></div>
                    </div>
                    <p class="text-xs text-muted-foreground md:text-right">
                      {{
                        adminText("usage.dailyLine", {
                          users: day.active_users,
                          attempts: day.quiz_attempts,
                          answers: day.answers,
                        })
                      }}
                    </p>
                  </div>
                  <p
                    *ngIf="dailyUsage.length === 0"
                    class="text-sm text-muted-foreground"
                  >
                    {{ adminText("usage.noActivity") }}
                  </p>
                </div>
              </section>

              <section [class]="cardClasses('p-6')">
                <header class="mb-5">
                  <h3 class="text-lg font-semibold">
                    {{ adminText("usage.byHub") }}
                  </h3>
                  <p class="text-sm text-muted-foreground">
                    {{ adminText("usage.byHubDescription") }}
                  </p>
                </header>
                <div class="space-y-4">
                  <div
                    *ngFor="let hubRow of hubUsage"
                    class="border-b border-border pb-4 last:border-0 last:pb-0"
                  >
                    <div class="flex items-center justify-between gap-3">
                      <p class="font-medium capitalize">{{ hubRow.hub }}</p>
                      <span [class]="badgeClasses('outline')">
                        {{
                          adminText("usage.hubUsers", {
                            count: hubRow.users,
                          })
                        }}
                      </span>
                    </div>
                    <div class="mt-3 h-2 overflow-hidden rounded-full bg-muted">
                      <div
                        class="h-full rounded-full bg-secondary-foreground/70"
                        [ngStyle]="barStyle(hubRow.users, maxHubUsers)"
                      ></div>
                    </div>
                    <p class="mt-2 text-xs text-muted-foreground">
                      {{
                        adminText("usage.hubLine", {
                          active: hubRow.active_users_30d,
                          score: hubRow.avg_score_percent,
                          islands: hubRow.completed_islands,
                        })
                      }}
                    </p>
                  </div>
                  <p
                    *ngIf="hubUsage.length === 0"
                    class="text-sm text-muted-foreground"
                  >
                    {{ adminText("usage.noHub") }}
                  </p>
                </div>
              </section>
            </div>

            <section [class]="cardClasses('p-6')">
              <header class="mb-5">
                <h3 class="text-lg font-semibold">
                  {{ adminText("usage.islandPerformance") }}
                </h3>
                <p class="text-sm text-muted-foreground">
                  {{ adminText("usage.islandPerformanceDescription") }}
                </p>
              </header>
              <div class="grid gap-5 lg:grid-cols-2">
                <div
                  *ngFor="let islandRow of islandPerformance"
                  class="border-b border-border pb-5 last:border-0 lg:border-b-0 lg:pb-0"
                >
                  <div class="flex items-start justify-between gap-3">
                    <div class="min-w-0">
                      <h4 class="truncate font-semibold">
                        {{ islandRow.name }}
                      </h4>
                      <p class="truncate text-xs text-muted-foreground">
                        {{ islandRow.slug }}
                      </p>
                    </div>
                    <span
                      [class]="
                        badgeClasses(signalBadgeVariant(islandRow.signal))
                      "
                    >
                      {{ usageSignalText(islandRow.signal) }}
                    </span>
                  </div>
                  <div class="mt-4 space-y-3">
                    <div>
                      <div class="mb-1 flex justify-between text-xs">
                        <span>{{ adminText("usage.accuracy") }}</span>
                        <span>{{ islandRow.correct_rate_percent }}%</span>
                      </div>
                      <div class="h-2 overflow-hidden rounded-full bg-muted">
                        <div
                          class="h-full rounded-full bg-primary"
                          [ngStyle]="percentBarStyle(islandRow.correct_rate_percent)"
                        ></div>
                      </div>
                    </div>
                    <div>
                      <div class="mb-1 flex justify-between text-xs">
                        <span>{{ adminText("usage.completion") }}</span>
                        <span>{{ islandRow.completion_rate_percent }}%</span>
                      </div>
                      <div class="h-2 overflow-hidden rounded-full bg-muted">
                        <div
                          class="h-full rounded-full bg-secondary-foreground/70"
                          [ngStyle]="
                            percentBarStyle(islandRow.completion_rate_percent)
                          "
                        ></div>
                      </div>
                    </div>
                  </div>
                  <p class="mt-3 text-xs text-muted-foreground">
                    {{
                      adminText("usage.islandStats", {
                        attempts: islandRow.attempts,
                        completions: islandRow.completions,
                        score: islandRow.avg_score_percent,
                      })
                    }}
                  </p>
                </div>
              </div>
              <p
                *ngIf="islandPerformance.length === 0"
                class="text-sm text-muted-foreground"
              >
                {{ adminText("usage.noIslandPerformance") }}
              </p>
            </section>

            <div class="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
              <section [class]="cardClasses('p-6')">
                <header class="mb-5">
                  <h3 class="text-lg font-semibold">
                    {{ adminText("usage.improvementPoints") }}
                  </h3>
                  <p class="text-sm text-muted-foreground">
                    {{ adminText("usage.improvementDescription") }}
                  </p>
                </header>
                <div class="space-y-4">
                  <div
                    *ngFor="let item of questionImprovements"
                    class="border-b border-border pb-4 last:border-0 last:pb-0"
                  >
                    <div class="flex items-start justify-between gap-3">
                      <p class="text-sm font-medium text-foreground">
                        {{ item.island_name }}
                      </p>
                      <span [class]="badgeClasses('outline')">
                        {{
                          adminText("usage.percentWrong", {
                            percent: item.incorrect_rate_percent,
                          })
                        }}
                      </span>
                    </div>
                    <p class="mt-2 text-sm text-muted-foreground">
                      {{ item.question_text }}
                    </p>
                    <p class="mt-2 text-xs font-medium text-foreground">
                      {{ improvementHintText(item.improvement_hint) }}
                    </p>
                    <p class="mt-1 text-xs text-muted-foreground">
                      {{
                        adminText("usage.incorrectLine", {
                          incorrect: item.incorrect,
                          responses: item.responses,
                        })
                      }}
                    </p>
                  </div>
                  <p
                    *ngIf="questionImprovements.length === 0"
                    class="text-sm text-muted-foreground"
                  >
                    {{ adminText("usage.noQuestionData") }}
                  </p>
                </div>
              </section>

              <section [class]="cardClasses('p-6')">
                <header class="mb-5">
                  <h3 class="text-lg font-semibold">
                    {{ adminText("usage.mostActiveUsers") }}
                  </h3>
                  <p class="text-sm text-muted-foreground">
                    {{ adminText("usage.mostActiveDescription") }}
                  </p>
                </header>
                <div class="overflow-x-auto">
                  <table class="w-full min-w-[42rem] text-sm">
                    <thead>
                      <tr class="border-b text-left text-xs text-muted-foreground">
                        <th class="pb-3 font-medium">
                          {{ adminText("usage.tableUser") }}
                        </th>
                        <th class="pb-3 font-medium">
                          {{ adminText("usage.tableProgress") }}
                        </th>
                        <th class="pb-3 font-medium">
                          {{ adminText("usage.tableScore") }}
                        </th>
                        <th class="pb-3 font-medium">
                          {{ adminText("usage.tableLastActivity") }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        *ngFor="let userRow of userUsage | slice: 0 : 8"
                        class="border-b last:border-0"
                      >
                        <td class="py-3 pr-4">
                          <p class="font-medium">{{ userRow.display_name }}</p>
                          <p class="text-xs text-muted-foreground">
                            {{ userRow.email || userRow.hub || userRow.user_id }}
                          </p>
                        </td>
                        <td class="py-3 pr-4">
                          <p>
                            {{
                              adminText("usage.userIslands", {
                                count: userRow.completed_islands,
                              })
                            }}
                          </p>
                          <p class="text-xs text-muted-foreground">
                            {{
                              adminText("usage.userAttemptStreak", {
                                attempts: userRow.quiz_attempts,
                                streak: userRow.current_streak,
                              })
                            }}
                          </p>
                        </td>
                        <td class="py-3 pr-4">
                          <div class="flex items-center gap-2">
                            <div class="h-2 w-24 overflow-hidden rounded-full bg-muted">
                              <div
                                class="h-full rounded-full bg-primary"
                                [ngStyle]="
                                  barStyle(userRow.total_points, maxUserPoints)
                                "
                              ></div>
                            </div>
                            <span>{{ userRow.total_points }}</span>
                          </div>
                          <p class="mt-1 text-xs text-muted-foreground">
                            {{
                              adminText("usage.averageShort", {
                                score: userRow.avg_score_percent,
                              })
                            }}
                          </p>
                        </td>
                        <td class="py-3">
                          {{
                            userRow.last_activity_at
                              ? (userRow.last_activity_at | date: "dd/MM HH:mm")
                              : adminText("usage.noLastActivity")
                          }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p
                  *ngIf="userUsage.length === 0"
                  class="text-sm text-muted-foreground"
                >
                  {{ adminText("usage.noUserActivity") }}
                </p>
              </section>
            </div>
          </ng-container>
        </section>

        <section
          *ngIf="activeAdminTab === 'users' && isAdmin"
          class="space-y-6"
        >
          <div [class]="cardClasses()">
            <header class="p-6">
              <h2 class="text-2xl font-semibold leading-none tracking-normal">
                {{ adminText("users.title") }}
              </h2>
              <p class="mt-2 text-sm text-muted-foreground">
                {{ adminText("users.description") }}
              </p>
            </header>
            <div class="p-6 pt-0 space-y-3">
              <p
                *ngIf="usersError"
                class="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive"
              >
                {{ usersError }}
              </p>
              <div
                *ngFor="let adminUser of adminUsers"
                class="flex flex-col gap-3 rounded-md border bg-card p-4 md:flex-row md:items-center md:justify-between"
              >
                <div class="min-w-0">
                  <div class="flex items-center gap-2">
                    <p class="font-medium truncate">
                      {{
                        adminUser.display_name ||
                          adminUser.email ||
                          adminUser.id
                      }}
                    </p>
                    <span
                      *ngIf="adminUser.role === 'admin'"
                      [class]="badgeClasses('default', 'gap-1')"
                    >
                      <svg lucideIcon="shield" class="h-3 w-3"></svg>
                      {{ adminText("users.admin") }}
                    </span>
                  </div>
                  <p class="text-sm text-muted-foreground truncate">
                    {{ adminUser.email || adminText("users.noEmail") }} -
                    {{ adminUser.id }}
                  </p>
                </div>
                <div class="flex flex-wrap gap-2">
                  <button
                    [class]="
                      buttonClasses(
                        adminUser.role === 'user' ? 'default' : 'outline'
                      )
                    "
                    [disabled]="
                      saving === adminUser.id ||
                      adminUser.role === 'user' ||
                      adminUser.id === user?.id
                    "
                    (click)="handleSetUserRole(adminUser, 'user')"
                  >
                    {{ adminText("users.user") }}
                  </button>
                  <button
                    [class]="
                      buttonClasses(
                        adminUser.role === 'admin' ? 'default' : 'outline'
                      )
                    "
                    [disabled]="
                      saving === adminUser.id || adminUser.role === 'admin'
                    "
                    (click)="handleSetUserRole(adminUser, 'admin')"
                  >
                    {{ adminText("users.admin") }}
                  </button>
                  <button
                    [class]="buttonClasses('destructive')"
                    [disabled]="
                      saving === adminUser.id || adminUser.id === user?.id
                    "
                    (click)="handleDeleteUser(adminUser)"
                  >
                    <svg lucideIcon="trash-2" class="h-4 w-4"></svg>
                    {{ adminText("action.delete") }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <div
        *ngIf="newIslandOpen"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      >
        <div
          [class]="cardClasses('w-full max-w-md max-h-[92vh] overflow-y-auto')"
        >
          <header class="p-6">
            <h2 class="text-2xl font-semibold leading-none tracking-normal">
              {{ adminText("content.createIslandTitle") }}
            </h2>
            <p class="mt-2 text-sm text-muted-foreground">
              {{ adminText("content.createIslandDescription") }}
            </p>
          </header>
          <div class="p-6 pt-0 space-y-4">
            <input
              [class]="inputClasses()"
              [(ngModel)]="newIsland.name"
              [placeholder]="adminText('content.requiredName')"
            />
            <input
              [class]="inputClasses()"
              [(ngModel)]="newIsland.slug"
              [placeholder]="adminText('content.requiredSlug')"
            />
            <textarea
              [class]="inputClasses('min-h-24')"
              [(ngModel)]="newIsland.description"
              [placeholder]="adminText('content.requiredDescription')"
            ></textarea>
            <input
              [class]="inputClasses()"
              [(ngModel)]="newIsland.icon"
              [placeholder]="adminText('content.icon')"
            />
            <input
              [class]="inputClasses()"
              [(ngModel)]="newIsland.image_url"
              [placeholder]="adminText('content.imageUrl')"
            />
            <label class="flex items-center gap-2">
              <input type="checkbox" [(ngModel)]="newIsland.is_active" />
              <span class="text-sm font-medium">{{
                adminText("status.active")
              }}</span>
            </label>
            <div class="flex justify-end gap-2 pt-2">
              <button
                [class]="buttonClasses('outline')"
                (click)="newIslandOpen = false"
              >
                {{ adminText("action.cancel") }}
              </button>
              <button
                [class]="buttonClasses('default')"
                (click)="handleCreateIsland()"
                [disabled]="creatingIsland"
              >
                {{
                  creatingIsland
                    ? adminText("action.creating")
                    : adminText("action.createIsland")
                }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
})
export class AdminIslandsPageComponent implements OnInit, OnDestroy {
  private readonly auth = inject(AuthService);
  readonly language = inject(LanguageService);
  readonly router = inject(Router);
  private readonly toast = inject(ToastService);
  private readonly changeDetector = inject(ChangeDetectorRef);

  user: User | null = null;
  authLoading = true;
  isAdmin: boolean | null = null;
  isCrewOwner = false;
  crewId: string | null = null;
  islands: Island[] = [];
  questions: Question[] = [];
  answers: Answer[] = [];
  adminUsers: AdminUser[] = [];
  loading = true;
  saving: string | null = null;
  questionsError: string | null = null;
  usersError: string | null = null;
  usageError: string | null = null;
  usageLoading = false;
  usageLoaded = false;
  usageSummary = emptyUsageSummary();
  dailyUsage: DailyUsageRow[] = [];
  islandPerformance: IslandPerformanceRow[] = [];
  questionImprovements: QuestionImprovementRow[] = [];
  userUsage: UserUsageRow[] = [];
  hubUsage: HubUsageRow[] = [];
  activeAdminTab: "content" | "usage" | "users" = "content";
  editedIslands: Record<string, Partial<Island>> = {};
  newIslandOpen = false;
  selectedIslandId = "";
  newQuestion = {
    question_text: "",
    question_text_translations: {} as LocalizedTextMap,
    explanation: "",
    explanation_translations: {} as LocalizedTextMap,
    stage: "1",
    point_value: "10",
  };
  newAnswers = emptyAnswerDrafts();
  newIsland = {
    name: "",
    name_translations: {} as LocalizedTextMap,
    slug: "",
    description: "",
    description_translations: {} as LocalizedTextMap,
    icon: "",
    image_url: "",
    is_active: true,
  };
  creatingIsland = false;
  creatingQuestion = false;
  uploadingImageFor: string | null = null;
  readonly translationLanguages = TRANSLATION_LANGUAGES;
  private subscription?: Subscription;
  private loadedAdminContextKey = "";
  private adminLoadSequence = 0;

  ngOnInit() {
    this.subscription = this.auth.state$.subscribe((state) => {
      this.user = state.user;
      this.authLoading = state.loading;
      this.isAdmin = state.isAdmin;
      this.isCrewOwner = state.isCrewOwner;
      this.crewId = state.crewId;
      if (
        !state.isAdmin &&
        (this.activeAdminTab === "users" || this.activeAdminTab === "usage")
      )
        this.activeAdminTab = "content";
      this.changeDetector.detectChanges();

      if (state.loading) return;

      if (!state.user) {
        void this.router.navigateByUrl("/auth");
        return;
      }

      if (state.isAdmin === false && !state.isCrewOwner) {
        this.toast.error(this.adminText("toast.accessDenied"));
        void this.router.navigateByUrl("/");
        return;
      }

      if (
        (state.isAdmin || state.isCrewOwner) &&
        (state.isAdmin || state.crewId)
      ) {
        const contextKey = `${state.user.id}:${state.isAdmin ? "admin" : "crew"}:${state.crewId ?? "global"}`;
        if (contextKey === this.loadedAdminContextKey) return;

        this.loadedAdminContextKey = contextKey;
        void this.fetchAdminData();
      }
    });
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  get selectedIsland() {
    return (
      this.islands.find((island) => island.id === this.selectedIslandId) || null
    );
  }

  get selectedQuestions() {
    return this.questions
      .filter((question) => question.island_id === this.selectedIslandId)
      .sort((a, b) => a.stage - b.stage || a.order_index - b.order_index);
  }

  async fetchAdminData() {
    const loadId = ++this.adminLoadSequence;
    this.loading = true;
    this.changeDetector.detectChanges();
    try {
      const loadedIslands = await this.fetchIslands();
      if (loadId !== this.adminLoadSequence) return;
      await this.fetchQuestionContent(loadedIslands);
      if (loadId !== this.adminLoadSequence) return;
      if (this.isAdmin) {
        await Promise.all([this.fetchAdminUsers(), this.fetchUsageAnalytics()]);
      }
      if (loadId !== this.adminLoadSequence) return;
      if (!this.selectedIslandId && this.islands.length > 0)
        this.selectedIslandId = this.islands[0].id;
    } finally {
      if (loadId === this.adminLoadSequence) {
        this.loading = false;
        this.changeDetector.detectChanges();
      }
    }
  }

  async fetchIslands() {
    try {
      if (!this.isAdmin && !this.crewId) {
        this.islands = [];
        return [];
      }

      if (!this.isAdmin && this.crewId) {
        const { count } = await backend
          .from("islands")
          .select("*", { count: "exact", head: true })
          .filter("crew_id", "eq", this.crewId);

        if (count === 0 || count === null) {
          const { error: cloneError } = await backend.rpc(
            "clone_islands_for_crew",
            { _crew_id: this.crewId },
          );
          if (cloneError) {
            console.error("Error cloning template islands:", cloneError);
            this.toast.error(this.adminText("toast.cloneError"));
          } else {
            this.toast.success(this.adminText("toast.cloneSuccess"));
          }
        }

        const result = await backend
          .from("islands")
          .select("*")
          .filter("crew_id", "eq", this.crewId)
          .order("order_index", { ascending: true });
        if (result.error) throw result.error;
        this.islands = (result.data || []) as Island[];
      } else {
        const result = await backend
          .from("islands")
          .select("*")
          .order("order_index", { ascending: true });
        if (result.error) throw result.error;
        this.islands = (result.data || []) as Island[];
      }

      return this.islands;
    } catch (error) {
      console.error("Error fetching islands:", error);
      this.toast.error(this.adminText("toast.loadIslandsError"));
      return [];
    }
  }

  async fetchQuestionContent(loadedIslands = this.islands) {
    this.questionsError = null;
    const islandIds = loadedIslands.map((island) => island.id);
    if (islandIds.length === 0) {
      this.questions = [];
      this.answers = [];
      return;
    }

    try {
      const { data: questionData, error: questionError } = await backend
        .from("questions")
        .select("*")
        .in("island_id", islandIds)
        .order("stage", { ascending: true })
        .order("order_index", { ascending: true });

      if (questionError) throw questionError;
      this.questions = (questionData || []) as Question[];

      if (this.questions.length === 0) {
        this.answers = [];
        return;
      }

      const answerRows: Answer[] = [];
      for (const questionIdChunk of chunkArray(
        this.questions.map((question) => question.id),
        120,
      )) {
        const { data: answerData, error: answerError } = await backend
          .from("answers")
          .select("*")
          .in("question_id", questionIdChunk)
          .order("order_index", { ascending: true });

        if (answerError) throw answerError;
        answerRows.push(...((answerData || []) as Answer[]));
      }

      this.answers = answerRows;
    } catch (error) {
      console.error("Error fetching questions:", error);
      this.questionsError = this.adminText("toast.questionsLoadError");
    }
  }

  async fetchAdminUsers() {
    this.usersError = null;
    const { data, error } = await backend.rpc("list_admin_users");
    if (error) {
      console.error("Error fetching users:", error);
      const fallbackLoaded = await this.fetchAdminUsersFallback();
      if (!fallbackLoaded) {
        this.usersError = this.adminText("toast.usersUnavailable");
      }
      return;
    }
    this.adminUsers = (data || []) as AdminUser[];
  }

  private async fetchAdminUsersFallback() {
    try {
      const [usersResult, profilesResult, rolesResult] = await Promise.all([
        backend
          .from("users")
          .select("id,email,created_at")
          .order("created_at", { ascending: false }),
        backend.from("profiles").select("id,display_name,avatar_url"),
        backend.from("user_roles").select("user_id,role"),
      ]);

      if (usersResult.error || profilesResult.error || rolesResult.error) {
        console.error("Error fetching fallback admin users:", {
          users: usersResult.error,
          profiles: profilesResult.error,
          roles: rolesResult.error,
        });
        return false;
      }

      const profiles = new Map(
        ((profilesResult.data || []) as AdminUserFallbackProfile[]).map(
          (profile) => [profile.id, profile],
        ),
      );
      const roles = new Map<string, "admin" | "user">();
      for (const role of (rolesResult.data || []) as AdminUserFallbackRole[]) {
        const currentRole = roles.get(role.user_id);
        if (role.role === "admin" || !currentRole) {
          roles.set(role.user_id, role.role);
        }
      }

      this.adminUsers = ((usersResult.data || []) as AdminUserFallbackUser[]).map(
        (appUser) => {
          const profile = profiles.get(appUser.id);
          return {
            id: appUser.id,
            email: appUser.email,
            display_name: profile?.display_name ?? null,
            avatar_url: profile?.avatar_url ?? null,
            created_at: appUser.created_at,
            role: roles.get(appUser.id) ?? "user",
          };
        },
      );

      return true;
    } catch (error) {
      console.error("Error loading fallback admin users:", error);
      return false;
    }
  }

  async fetchUsageAnalytics() {
    if (!this.isAdmin) return;

    this.usageError = null;
    this.usageLoading = true;
    this.changeDetector.detectChanges();

    try {
      const { data, error } = await backend.rpc("get_admin_usage_analytics");
      if (error) throw error;

      const sections = new Map(
        ((data || []) as UsageAnalyticsRow[]).map((row) => [
          row.section,
          row.payload,
        ]),
      );

      this.usageSummary = {
        ...emptyUsageSummary(),
        ...((sections.get("summary") as Partial<UsageSummary>) || {}),
      };
      this.dailyUsage = sectionArray<DailyUsageRow>(sections, "daily_usage");
      this.islandPerformance = sectionArray<IslandPerformanceRow>(
        sections,
        "island_performance",
      );
      this.questionImprovements = sectionArray<QuestionImprovementRow>(
        sections,
        "question_improvements",
      );
      this.userUsage = sectionArray<UserUsageRow>(sections, "user_usage");
      this.hubUsage = sectionArray<HubUsageRow>(sections, "hub_usage");
      this.usageLoaded = true;
    } catch (error) {
      console.error("Error fetching usage analytics:", error);
      this.usageError = this.adminText("toast.usageUnavailable");
    } finally {
      this.usageLoading = false;
      this.changeDetector.detectChanges();
    }
  }

  async uploadIslandImage(file: File | null, target: "new" | string) {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      this.toast.error(this.adminText("toast.imageOnly"));
      return;
    }

    this.uploadingImageFor = target;
    this.changeDetector.detectChanges();
    try {
      const extension = file.name.split(".").pop()?.toLowerCase() || "png";
      const path = `${this.isAdmin ? "global" : this.crewId || "crew"}/${crypto.randomUUID()}.${extension}`;
      const { error: uploadError } = await backend.storage
        .from("island-images")
        .upload(path, file, { contentType: file.type, upsert: false });
      if (uploadError) throw uploadError;
      const { data } = backend.storage
        .from("island-images")
        .getPublicUrl(path);
      const imageUrl = data.publicUrl;

      if (target === "new") {
        this.newIsland.image_url = imageUrl;
      } else {
        const { error } = await backend
          .from("islands")
          .update({ image_url: imageUrl })
          .eq("id", target);
        if (error) throw error;
        this.islands = this.islands.map((island) =>
          island.id === target ? { ...island, image_url: imageUrl } : island,
        );
      }

      this.toast.success(this.adminText("toast.imageUploaded"));
    } catch (error) {
      console.error("Error uploading island image:", error);
      this.toast.error(this.adminText("toast.imageUploadError"));
    } finally {
      this.uploadingImageFor = null;
      this.changeDetector.detectChanges();
    }
  }

  draftIsland(island: Island): Island {
    return { ...island, ...this.editedIslands[island.id] };
  }

  handleFieldChange(
    islandId: string,
    field: keyof Island,
    value: Island[keyof Island],
  ) {
    const island = this.islands.find((item) => item.id === islandId);
    const nextChanges: Partial<Island> = {
      ...this.editedIslands[islandId],
      [field]: value,
    };

    if (field === "name" && typeof value === "string") {
      nextChanges.name_translations = withEnglishFallback(
        nextChanges.name_translations ?? island?.name_translations,
        value,
      );
    }
    if (field === "description" && typeof value === "string") {
      nextChanges.description_translations = withEnglishFallback(
        nextChanges.description_translations ??
          island?.description_translations,
        value,
      );
    }

    this.editedIslands = { ...this.editedIslands, [islandId]: nextChanges };
  }

  setIslandTranslation(
    islandId: string,
    field: "name_translations" | "description_translations",
    language: LanguageCode,
    value: string,
  ) {
    const island = this.draftIsland(
      this.islands.find((item) => item.id === islandId)!,
    );
    this.handleFieldChange(
      islandId,
      field,
      setLocalizedText(island[field], language, value) as Island[keyof Island],
    );
  }

  async handleSave(islandId: string) {
    const changes = this.editedIslands[islandId];
    if (!changes) return;
    this.saving = islandId;
    this.changeDetector.detectChanges();

    try {
      const { error } = await backend
        .from("islands")
        .update(changes)
        .eq("id", islandId);
      if (error) throw error;
      this.islands = this.islands.map((island) =>
        island.id === islandId ? { ...island, ...changes } : island,
      );
      const next = { ...this.editedIslands };
      delete next[islandId];
      this.editedIslands = next;
      this.toast.success(this.adminText("toast.islandUpdated"));
    } catch (error) {
      console.error("Error saving island:", error);
      this.toast.error(this.adminText("toast.saveError"));
    } finally {
      this.saving = null;
      this.changeDetector.detectChanges();
    }
  }

  async handleCreateIsland() {
    if (
      !this.newIsland.name ||
      !this.newIsland.slug ||
      !this.newIsland.description
    ) {
      this.toast.error(this.adminText("toast.requiredFields"));
      return;
    }
    if (!this.isAdmin && !this.crewId) {
      this.toast.error(this.adminText("toast.crewRequired"));
      return;
    }

    this.creatingIsland = true;
    this.changeDetector.detectChanges();
    try {
      const maxOrderIndex =
        this.islands.length > 0
          ? Math.max(...this.islands.map((island) => island.order_index))
          : -1;
      const { data, error } = await backend
        .from("islands")
        .insert({
          name: this.newIsland.name,
          name_translations: withEnglishFallback(
            this.newIsland.name_translations,
            this.newIsland.name,
          ),
          slug: this.newIsland.slug.toLowerCase().replace(/\s+/g, "-"),
          description: this.newIsland.description,
          description_translations: withEnglishFallback(
            this.newIsland.description_translations,
            this.newIsland.description,
          ),
          icon: this.newIsland.icon || null,
          image_url: this.newIsland.image_url || null,
          is_active: this.newIsland.is_active,
          order_index: maxOrderIndex + 1,
          crew_id: this.isAdmin ? null : this.crewId,
        })
        .select()
        .single();

      if (error) throw error;
      this.islands = [...this.islands, data as Island];
      this.newIsland = {
        name: "",
        name_translations: {},
        slug: "",
        description: "",
        description_translations: {},
        icon: "",
        image_url: "",
        is_active: true,
      };
      this.newIslandOpen = false;
      this.toast.success(this.adminText("toast.islandCreated"));
    } catch (error: any) {
      console.error("Error creating island:", error);
      this.toast.error(
        error?.code === "23505"
          ? this.adminText("toast.duplicateSlug")
          : this.adminText("toast.createIslandError"),
      );
    } finally {
      this.creatingIsland = false;
      this.changeDetector.detectChanges();
    }
  }

  async handleDeleteIsland(islandId: string, islandName: string) {
    if (
      !confirm(
        this.adminText("toast.deleteIslandConfirm", { name: islandName }),
      )
    )
      return;
    try {
      const { error } = await backend
        .from("islands")
        .delete()
        .eq("id", islandId);
      if (error) throw error;
      this.islands = this.islands.filter((island) => island.id !== islandId);
      this.questions = this.questions.filter(
        (question) => question.island_id !== islandId,
      );
      this.selectedIslandId = this.islands[0]?.id || "";
      this.toast.success(this.adminText("toast.islandDeleted"));
    } catch (error) {
      console.error("Error deleting island:", error);
      this.toast.error(this.adminText("toast.deleteIslandError"));
    }
  }

  async handleCreateQuestion() {
    if (!this.selectedIslandId || !this.newQuestion.question_text.trim()) {
      this.toast.error(this.adminText("toast.selectQuestion"));
      return;
    }
    const filledAnswers = this.newAnswers.filter((answer) =>
      answer.answer_text.trim(),
    );
    if (filledAnswers.length < 2) {
      this.toast.error(this.adminText("toast.addAnswers"));
      return;
    }
    if (!filledAnswers.some((answer) => answer.is_correct)) {
      this.toast.error(this.adminText("toast.markCorrect"));
      return;
    }

    this.creatingQuestion = true;
    this.changeDetector.detectChanges();
    try {
      const stage = Number(this.newQuestion.stage);
      const pointValue = normalizeQuestionPointValue(
        this.newQuestion.point_value,
      );
      const maxOrderIndex = this.questions
        .filter(
          (question) =>
            question.island_id === this.selectedIslandId &&
            question.stage === stage,
        )
        .reduce((max, question) => Math.max(max, question.order_index), 0);

      const { data: questionData, error: questionError } = await backend
        .from("questions")
        .insert({
          island_id: this.selectedIslandId,
          question_text: this.newQuestion.question_text.trim(),
          question_text_translations: withEnglishFallback(
            this.newQuestion.question_text_translations,
            this.newQuestion.question_text,
          ),
          explanation: this.newQuestion.explanation.trim() || null,
          explanation_translations: withEnglishFallback(
            this.newQuestion.explanation_translations,
            this.newQuestion.explanation,
          ),
          stage,
          order_index: maxOrderIndex + 1,
          point_value: pointValue,
        })
        .select()
        .single();

      if (questionError) throw questionError;

      const { error: answersError } = await backend.from("answers").insert(
        filledAnswers.map((answer, index) => ({
          question_id: questionData.id,
          answer_text: answer.answer_text.trim(),
          answer_text_translations: withEnglishFallback(
            answer.answer_text_translations,
            answer.answer_text,
          ),
          is_correct: answer.is_correct,
          order_index: index + 1,
        })),
      );
      if (answersError) throw answersError;

      this.newQuestion = {
        question_text: "",
        question_text_translations: {},
        explanation: "",
        explanation_translations: {},
        stage: this.newQuestion.stage,
        point_value: "10",
      };
      this.newAnswers = emptyAnswerDrafts();
      await this.fetchQuestionContent();
      this.toast.success(this.adminText("toast.questionCreated"));
    } catch (error) {
      console.error("Error creating question:", error);
      this.toast.error(this.adminText("toast.createQuestionError"));
    } finally {
      this.creatingQuestion = false;
      this.changeDetector.detectChanges();
    }
  }

  handleQuestionChange(
    questionId: string,
    field: keyof Question,
    value: Question[keyof Question],
  ) {
    this.questions = this.questions.map((question) => {
      if (question.id !== questionId) return question;
      const nextQuestion = { ...question, [field]: value };
      if (field === "question_text" && typeof value === "string") {
        nextQuestion.question_text_translations = withEnglishFallback(
          nextQuestion.question_text_translations,
          value,
        );
      }
      if (
        field === "explanation" &&
        (typeof value === "string" || value === null)
      ) {
        nextQuestion.explanation_translations = withEnglishFallback(
          nextQuestion.explanation_translations,
          value as string | null,
        );
      }
      return nextQuestion;
    });
  }

  handleAnswerChange(answerId: string, value: string) {
    this.answers = this.answers.map((answer) =>
      answer.id === answerId
        ? {
            ...answer,
            answer_text: value,
            answer_text_translations: withEnglishFallback(
              answer.answer_text_translations,
              value,
            ),
          }
        : answer,
    );
  }

  markAnswerCorrect(questionId: string, answerId: string) {
    this.answers = this.answers.map((answer) =>
      answer.question_id === questionId
        ? { ...answer, is_correct: answer.id === answerId }
        : answer,
    );
  }

  async handleSaveQuestion(question: Question) {
    this.saving = question.id;
    this.changeDetector.detectChanges();
    try {
      const { error: questionError } = await backend
        .from("questions")
        .update({
          question_text: question.question_text,
          question_text_translations: withEnglishFallback(
            question.question_text_translations,
            question.question_text,
          ),
          explanation: question.explanation?.trim() || null,
          explanation_translations: withEnglishFallback(
            question.explanation_translations,
            question.explanation,
          ),
          stage: Number(question.stage),
          point_value: normalizeQuestionPointValue(question.point_value),
        })
        .eq("id", question.id);
      if (questionError) throw questionError;

      for (const answer of this.answersForQuestion(question.id)) {
        const { error: answerError } = await backend
          .from("answers")
          .update({
            answer_text: answer.answer_text,
            answer_text_translations: withEnglishFallback(
              answer.answer_text_translations,
              answer.answer_text,
            ),
            is_correct: answer.is_correct,
          })
          .eq("id", answer.id);
        if (answerError) throw answerError;
      }
      this.toast.success(this.adminText("toast.questionUpdated"));
    } catch (error) {
      console.error("Error saving question:", error);
      this.toast.error(this.adminText("toast.saveQuestionError"));
    } finally {
      this.saving = null;
      this.changeDetector.detectChanges();
    }
  }

  async handleDeleteQuestion(questionId: string) {
    if (!confirm(this.adminText("toast.deleteQuestionConfirm"))) return;
    try {
      const { error } = await backend
        .from("questions")
        .delete()
        .eq("id", questionId);
      if (error) throw error;
      this.questions = this.questions.filter(
        (question) => question.id !== questionId,
      );
      this.answers = this.answers.filter(
        (answer) => answer.question_id !== questionId,
      );
      this.toast.success(this.adminText("toast.questionDeleted"));
    } catch (error) {
      console.error("Error deleting question:", error);
      this.toast.error(this.adminText("toast.deleteQuestionError"));
    }
  }

  async handleSetUserRole(targetUser: AdminUser, role: "admin" | "user") {
    this.saving = targetUser.id;
    this.changeDetector.detectChanges();
    try {
      const { error } = await backend.rpc("set_user_app_role", {
        p_user_id: targetUser.id,
        p_role: role,
      });
      if (error) throw error;
      this.adminUsers = this.adminUsers.map((user) =>
        user.id === targetUser.id ? { ...user, role } : user,
      );
      await this.fetchAdminUsers();
      this.toast.success(this.adminText("toast.roleUpdated"));
    } catch (error) {
      console.error("Error setting user role:", error);
      this.toast.error(this.adminText("toast.roleUpdateError"));
    } finally {
      this.saving = null;
      this.changeDetector.detectChanges();
    }
  }

  async handleDeleteUser(targetUser: AdminUser) {
    if (targetUser.id === this.user?.id) {
      this.toast.error(this.adminText("toast.deleteOwnAdmin"));
      return;
    }

    const label = targetUser.email || targetUser.display_name || targetUser.id;
    if (
      !confirm(
        this.adminText("toast.deleteUserConfirm", { name: label }),
      )
    )
      return;

    this.saving = targetUser.id;
    this.changeDetector.detectChanges();
    try {
      const { error } = await backend.rpc("delete_app_user", {
        p_user_id: targetUser.id,
      });
      if (error) throw error;
      this.adminUsers = this.adminUsers.filter(
        (adminUser) => adminUser.id !== targetUser.id,
      );
      await this.fetchAdminUsers();
      this.toast.success(this.adminText("toast.userDeleted"));
    } catch (error) {
      console.error("Error deleting user:", error);
      this.toast.error(this.adminText("toast.deleteUserError"));
    } finally {
      this.saving = null;
      this.changeDetector.detectChanges();
    }
  }

  answersForQuestion(questionId: string) {
    return this.answers
      .filter((answer) => answer.question_id === questionId)
      .sort((a, b) => a.order_index - b.order_index);
  }

  setNewCorrectAnswer(index: number) {
    this.newAnswers = this.newAnswers.map((answer, answerIndex) => ({
      ...answer,
      is_correct: answerIndex === index,
    }));
  }

  getTranslation(translations: LocalizedTextMap, language: LanguageCode) {
    return normalizeLocalizedTextMap(translations)[language] || "";
  }

  numberValue(value: string | number) {
    return Number(value);
  }

  adminText(key: AdminTextKey, params?: Record<string, string | number>) {
    const translated = ADMIN_TEXT[this.language.language]?.[key];
    return interpolateAdminText(translated || ADMIN_TEXT_EN[key] || key, params);
  }

  usageSignalText(signal: string) {
    switch (signal) {
      case "Saudavel":
      case "Healthy":
        return this.adminText("usage.signal.healthy");
      case "Sem uso ainda":
      case "No usage yet":
        return this.adminText("usage.signal.noUsage");
      case "Pontuacao media baixa":
      case "Low average score":
        return this.adminText("usage.signal.lowScore");
      case "Muitas respostas incorretas":
      case "Many incorrect answers":
        return this.adminText("usage.signal.incorrect");
      case "Baixa conclusao":
      case "Low completion":
        return this.adminText("usage.signal.lowCompletion");
      default:
        return signal;
    }
  }

  improvementHintText(hint: string) {
    switch (hint) {
      case "Coletar mais respostas":
      case "Collect more answers":
        return this.adminText("usage.hint.collectMore");
      case "Revisar enunciado e alternativas":
      case "Review prompt and alternatives":
        return this.adminText("usage.hint.review");
      case "Adicionar reforco ou exemplo":
      case "Add reinforcement or example":
        return this.adminText("usage.hint.addSupport");
      case "Monitorar tendencia":
      case "Monitor trend":
        return this.adminText("usage.hint.monitor");
      default:
        return hint;
    }
  }

  dailyTotal(day: DailyUsageRow) {
    return (
      Number(day.active_users || 0) +
      Number(day.quiz_attempts || 0) +
      Number(day.answers || 0) +
      Number(day.island_completions || 0) +
      Number(day.self_assessments || 0)
    );
  }

  get maxDailyTotal() {
    return Math.max(1, ...this.dailyUsage.map((day) => this.dailyTotal(day)));
  }

  get maxHubUsers() {
    return Math.max(1, ...this.hubUsage.map((hub) => Number(hub.users || 0)));
  }

  get maxUserPoints() {
    return Math.max(
      1,
      ...this.userUsage.map((userRow) => Number(userRow.total_points || 0)),
    );
  }

  barStyle(value: number, max: number) {
    const numericValue = Math.max(0, Number(value) || 0);
    const maxValue = Math.max(0, Number(max) || 0);
    const width = maxValue > 0 ? (numericValue / maxValue) * 100 : 0;

    return { width: `${Math.min(100, width)}%` };
  }

  percentBarStyle(value: number) {
    const width = Math.max(0, Math.min(100, Number(value) || 0));
    return { width: `${width}%` };
  }

  signalBadgeVariant(
    signal: string,
  ): "default" | "secondary" | "outline" | "destructive" {
    if (signal === "Saudavel") return "secondary";
    if (signal === "Sem uso ainda") return "outline";
    return "destructive";
  }

  buttonClasses = buttonClasses;
  cardClasses = cardClasses;
  inputClasses = inputClasses;
  labelClasses = labelClasses;
  badgeClasses = badgeClasses;
}
